import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './client'

interface QueryState<T> {
  data: T | undefined
  error: unknown
  loading: boolean
  reload: () => void
}

/**
 * Fetches on mount and whenever a dependency changes. Deliberately not a caching library — the
 * screens here are read-then-act, and a stale cache on a ledger is worse than a second request.
 */
export function useQuery<T>(path: string | null, deps: unknown[] = []): QueryState<T> {
  const [data, setData] = useState<T>()
  const [error, setError] = useState<unknown>()
  const [loading, setLoading] = useState(path !== null)
  const [nonce, setNonce] = useState(0)

  // Guards against a slow first response overwriting a fast second one.
  const latest = useRef(0)

  useEffect(() => {
    if (path === null) {
      setLoading(false)
      return
    }

    const ticket = ++latest.current
    const controller = new AbortController()

    setLoading(true)
    setError(undefined)

    void (async () => {
      try {
        const result = await api<T>(path, { signal: controller.signal })
        if (ticket === latest.current) {
          setData(result)
          setError(undefined)
        }
      } catch (caught) {
        if (controller.signal.aborted) return
        if (ticket === latest.current) setError(caught)
      } finally {
        if (ticket === latest.current) setLoading(false)
      }
    })()

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return { data, error, loading, reload }
}

/**
 * Wraps a write. Tracks the in-flight state and the error so every form does not reimplement the
 * same three useStates.
 */
export function useAction<TArgs extends unknown[], TResult>(
  action: (...args: TArgs) => Promise<TResult>,
) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  const run = useCallback(
    async (...args: TArgs): Promise<TResult | undefined> => {
      setBusy(true)
      setError(undefined)
      try {
        return await action(...args)
      } catch (caught) {
        setError(caught)
        return undefined
      } finally {
        setBusy(false)
      }
    },
    [action],
  )

  return { run, busy, error, clearError: () => setError(undefined) }
}
